<script lang="ts">
	import { browser } from '$app/environment';
	import Markdown from '$lib/Markdown.svelte';
	import ScrollGradient from '@astlai/common/ScrollGradient.svelte';
	import type { CitedSource, DocumentDTO } from 'src/types/llm';
	import { transformSourceText, type CitationHighlight } from 'src/util/citation-transformer';
	import { onMount } from 'svelte';
	import IconText from 'virtual:icons/material-symbols/article-outline';
	import CloseIcon from 'virtual:icons/material-symbols/close-rounded';
	import FormatQuoteIcon from 'virtual:icons/material-symbols/format-quote-rounded';
	import IconMetadata from 'virtual:icons/material-symbols/info-outline-rounded';
	import LinkIcon from 'virtual:icons/material-symbols/link';
	import IconPdf from 'virtual:icons/material-symbols/picture-as-pdf-outline-rounded';
	import IconSummary from 'virtual:icons/material-symbols/summarize-outline-rounded';
	import DocumentTitle from './DocumentTitle.svelte';
	import { t } from './i18n/tranlsator.svelte';
	import MetadataList from './MetadataList.svelte';
	import type PdfViewerType from './PdfViewer.svelte';
	import SourceBadge from './SourceBadge.svelte';
	import Tab from './tabs/Tab.svelte';
	import TabNav from './tabs/TabNav.svelte';
	import TabNavItem from './tabs/TabNavItem.svelte';
	import Tabs from './tabs/Tabs.svelte';
	import { tooltip } from './tooltip/tootlip.svelte';

	interface Props {
		document?: DocumentDTO;
		source?: CitedSource;
		showBackButton?: boolean;
		back?: () => void;
		selectedCitationHash?: string;
	}
	let { document, source, showBackButton, back = () => {}, selectedCitationHash }: Props = $props();

	let containerEl: HTMLDivElement = $state(null);
	let PdfViewer: typeof PdfViewerType | null = $state(null);
	const isWebSource = $derived(source?.web_id != null);
	let defaultActiveTab = $derived(isWebSource ? 'summary' : 'text');
	let activeTab = $state(defaultActiveTab);

	const sourceUrl = $derived(
		document?.href || document?.resource?.href || source?.url || document.res_href
	);

	const isPdf = $derived(sourceUrl?.endsWith('.pdf') ?? false);

	// Flatten source occurrences into CitationHighlight[] for transformSourceText
	const highlights = $derived<CitationHighlight[]>(
		source
			? source.occurrences.map((occ) => ({
					hash: occ.hash,
					citation_index: source.citation_index,
					citation_color: source.citation_color,
					sub_index: occ.sub_index,
					quotedText: occ.quotedText,
					chunkText: occ.chunkText,
					sourceBlocks: occ.sourceBlocks,
					type: occ.type,
					id: occ.id
				}))
			: []
	);

	const pdfHighlightText = $derived.by(() => {
		if (!isPdf || !selectedCitationHash || !highlights.length) {
			return null;
		}
		const h = highlights.find((q) => q.hash === selectedCitationHash);
		return h?.sourceBlocks ? null : (h?.quotedText ?? null);
	});

	const pdfHighlightBlocks = $derived.by(() => {
		if (!isPdf || !selectedCitationHash || !highlights.length) {
			return null;
		}
		const h = highlights.find((q) => q.hash === selectedCitationHash);
		return h?.sourceBlocks ?? null;
	});

	const transformedText = $derived(
		highlights.length && document ? transformSourceText(document.text, highlights) : document?.text
	);

	const hostname = $derived.by(() => {
		if (!source?.url) {
			return null;
		}
		try {
			return new URL(source.url).hostname;
		} catch {
			return source.url;
		}
	});

	const sourceUrlWithFragments = $derived.by(() => {
		if (!isWebSource || !source) {
			return sourceUrl;
		}

		const quotedOccurrences = source.occurrences.filter((occ) => occ.quotedText);
		if (!quotedOccurrences.length) {
			return sourceUrl;
		}

		const fragments = quotedOccurrences.map((occ) => {
			const words = occ.quotedText!.trim().split(/\s+/);
			if (words.length > 4) {
				const start = words.slice(0, 2).join(' ');
				const end = words.slice(-2).join(' ');
				return `${encodeURIComponent(start)},${encodeURIComponent(end)}`;
			}
			return encodeURIComponent(occ.quotedText!.trim());
		});
		return `${source.url}#:~:text=${fragments.join('&text=')}`;
	});

	const webSourceMetadata = $derived({
		url: source?.url,
		type: source?.web_source_type,
		...(source?.fetched_at ? { fetched_at: source.fetched_at } : {})
	});

	function scrollRefIntoView(citationHash?: string | null) {
		if (!citationHash || !containerEl) {
			return;
		}
		containerEl
			.querySelector(`[data-hash="${citationHash}"]`)
			?.scrollIntoView({ behavior: 'smooth', block: 'center' });
	}

	$effect(() => {
		if (selectedCitationHash) {
			scrollRefIntoView(selectedCitationHash);
		}
	});

	onMount(async () => {
		setTimeout(() => scrollRefIntoView(selectedCitationHash), 250);
		if (isPdf) {
			const mod = await import('./PdfViewer.svelte');
			PdfViewer = mod.default;
		}
	});
</script>

<div class="document-detail">
	<div class="source-header">
		{#if isWebSource && source}
			<SourceBadge
				badge={{ color: source.citation_color, index: source.citation_index }}
				webSource
				appearance="large"
			/>
			<div class="title-area">
				<span class="title">{source.title}</span>
				<span class="hostname">{hostname}</span>
			</div>
		{:else}
			{#if source}
				<SourceBadge
					badge={{ color: source.citation_color, index: source.citation_index }}
					appearance="large"
				/>
			{/if}
			<DocumentTitle doc={document} />
		{/if}

		<div class="header-actions">
			{#if sourceUrlWithFragments}
				<a
					href={sourceUrlWithFragments}
					target="_blank"
					rel="noopener"
					class="button icon-button"
					use:tooltip={isWebSource ? t('sources.openWebSource') : t('sources.documentLink')}
				>
					<LinkIcon />
				</a>
			{/if}

			{#if showBackButton}
				<button class="icon-button" onclick={() => back()}><CloseIcon /></button>
			{/if}
		</div>
	</div>

	<div class="tab-nav">
		<TabNav autoHideLabels {activeTab} onchange={(id) => (activeTab = id)}>
			{#if document}
				<TabNavItem id="text">
					{#snippet icon()}<IconText />{/snippet}
					{t('sources.content')}
				</TabNavItem>
			{/if}
			<TabNavItem id="summary">
				{#snippet icon()}<IconSummary />{/snippet}
				{t('sources.summary')}
			</TabNavItem>
			{#if document && isPdf}
				<TabNavItem id="pdf">
					{#snippet icon()}<IconPdf />{/snippet}
					{t('sources.pdfPreview')}
				</TabNavItem>
			{/if}
			<TabNavItem id="metadata">
				{#snippet icon()}<IconMetadata />{/snippet}
				{t('sources.metadata')}
			</TabNavItem>
		</TabNav>
	</div>

	<ScrollGradient align="bottom" colorVar="--surface-2" />
	<div bind:this={containerEl} class="content">
		{#if isWebSource && source}
			<Tabs {activeTab}>
				<Tab id="summary">
					{#if source.description}
						<div class="section">
							<h3>{t('sources.description')}</h3>
							<p>{source.description}</p>
						</div>
					{/if}

					{#if source.occurrences.some((occ) => occ.quotedText)}
						<div class="section">
							<h3>{t('sources.citedPassages')}</h3>
							<div class="quotes">
								{#each source.occurrences.filter((occ) => occ.quotedText) as occ (occ.hash)}
									<div class="quote-item">
										{#if occ.sub_index}
											<SourceBadge
												badge={{
													color: source.citation_color,
													index: source.citation_index,
													subIndex: occ.sub_index
												}}
												webSource
											/>
										{/if}
										<span class="quoted-text">
											<FormatQuoteIcon class="quote-icon quote-icon-open" />
											...{occ.quotedText}...
											<FormatQuoteIcon class="quote-icon quote-icon-close" />
										</span>
									</div>
								{/each}
							</div>
							<p class="copyright-notice">{t('sources.copyrightNotice')}</p>
						</div>
					{/if}
				</Tab>
				<Tab id="metadata">
					<MetadataList metadata={webSourceMetadata} />
				</Tab>
			</Tabs>
		{:else if document}
			<Tabs {activeTab}>
				<Tab id="summary">
					<div class="section">
						<div class="summary">
							{#if document.summary}
								<Markdown source={document.summary} />
							{:else}
								<p class="empty">{t('sources.summaryNotAvailable')}</p>
							{/if}
						</div>
					</div>
				</Tab>
				<Tab id="text">
					<div class="section">
						<div class="original">
							<Markdown source={transformedText} />
						</div>
					</div>
				</Tab>
				{#if isPdf}
					<Tab id="pdf">
						{#if browser && PdfViewer}
							<PdfViewer
								url={sourceUrl}
								highlightText={pdfHighlightText}
								highlightBlocks={pdfHighlightBlocks}
							/>
						{/if}
					</Tab>
				{/if}
				<Tab id="metadata">
					<MetadataList metadata={{ URL: sourceUrl }} />
					{#if document.meta}
						<MetadataList metadata={document.meta} />
					{/if}
					{#if document.resource?.meta && Object.keys(document.resource.meta).length > 0}
						<MetadataList metadata={document.resource.meta} />
					{/if}
				</Tab>
			</Tabs>
		{/if}
	</div>

	<ScrollGradient align="top" colorVar="--surface-2" />
</div>

<style lang="scss">
	@use '@astlai/common/styles/mixins' as lib;

	.document-detail {
		@include lib.flex-column();
		overflow: hidden;
		margin-top: 0;
		border-radius: 1rem;
		border: 1px solid var(--border-subtle);
		background: var(--surface-2);

		h3 {
			margin: 0;
			font-size: 1rem;
			line-height: 1rem;
		}

		.source-header {
			@include lib.flex-row();
			@include lib.flex-align();
			gap: 0.5rem;
			flex: 0 0 auto;
			padding: 1rem;
			position: sticky;
			top: 0px;

			.title-area {
				flex: 1 1 auto;
				@include lib.flex-column();
				gap: 0.125rem;
				overflow: hidden;

				.title {
					@include lib.ellipsis();
					font-weight: 500;
				}

				.hostname {
					@include lib.ellipsis();
					font-size: 0.75rem;
					color: var(--text-secondary);
				}
			}

			.header-actions {
				flex: 0 0 auto;
				@include lib.flex-row();
				@include lib.flex-align();
				gap: 0.5rem;
			}
		}

		.tab-nav {
			@include lib.flex-row();
			overflow: hidden;
			margin: 0 1rem;
			flex: 0 0 auto;
		}

		.content {
			flex: 1 1 auto;
			overflow: auto;
			overflow: overlay;
			@include lib.flex-column();
			gap: 1rem;
			font-size: 0.875rem;
			line-height: 1.5rem;
			padding: 1rem;
			position: relative;

			.source-link {
				@include lib.flex-row();
				@include lib.flex-align();
				gap: 0.25rem;
				height: auto;
				padding-left: 0.5rem;

				.icon {
					padding: 0 0.25rem;
					flex: 0 0 auto;
					display: flex;
				}

				.text {
					@include lib.flex-column();
					@include lib.flex-align(flex-start, stretch);
					gap: 0.25rem;
					overflow: hidden;

					span {
						@include lib.ellipsis();
						text-align: left;

						&.label {
							font-weight: 500;
							font-size: 1rem;
							line-height: 1rem;
						}

						&.url {
							font-size: 0.75rem;
							line-height: 0.75rem;
						}
					}
				}
			}

			.section {
				@include lib.flex-column();

				h3 {
					margin-bottom: 1rem;
				}

				p {
					margin: 0;
				}

				.copyright-notice {
					margin-top: 0.75rem;
					font-size: 0.75rem;
					color: var(--text-secondary);
					font-style: italic;
				}

				.empty {
					color: var(--text-secondary);
					font-style: italic;
				}
			}

			.quotes {
				@include lib.flex-column();
				gap: 0.75rem;

				.quote-item {
					.quoted-text {
						font-style: italic;

						:global(.quote-icon) {
							font-size: 0.875rem;
							vertical-align: middle;
							opacity: 0.5;
						}

						:global(.quote-icon-close) {
							transform: rotate(180deg);
							display: inline-block;
							vertical-align: top;
						}
					}

					.highlight-link {
						font-size: 0.75rem;
						padding-left: 0.75rem;
					}
				}
			}
		}
	}
</style>
